/**
 * Build a 2D flight path from already-generated application strips.
 *
 * Input points use GeoJSON order [longitude, latitude] by default. Set
 * coordinateOrder to "leaflet" for [latitude, longitude]. Point objects with
 * { lat, lng } are also accepted. The returned path always uses Leaflet order.
 * Segments with the same explicit key form one strip; gaps are crossed straight.
 */
function buildFlightPath2D(input) {
    input = input || {};

    function finite(value) {
        const number = Number(value);
        return Number.isFinite(number) ? number : null;
    }

    function normalizePoint(raw, coordinateOrder) {
        if (Array.isArray(raw) && raw.length >= 2) {
            const first = finite(raw[0]);
            const second = finite(raw[1]);
            if (first === null || second === null) return null;

            return coordinateOrder === 'leaflet'
                ? [first, second]
                : [second, first];
        }

        if (raw && typeof raw === 'object') {
            const lat = finite(raw.lat !== undefined ? raw.lat : raw.latitude);
            const lng = finite(raw.lng !== undefined ? raw.lng : (raw.lon !== undefined ? raw.lon : raw.longitude));
            return lat === null || lng === null ? null : [lat, lng];
        }

        return null;
    }

    function distanceSquared(pointA, pointB) {
        const meanLatitude = ((pointA[0] + pointB[0]) / 2) * Math.PI / 180;
        const latitudeDelta = pointA[0] - pointB[0];
        const longitudeDelta = (pointA[1] - pointB[1]) * Math.cos(meanLatitude);
        return (latitudeDelta * latitudeDelta) + (longitudeDelta * longitudeDelta);
    }

    function projection(referencePoint) {
        const radians = Math.PI / 180;
        const metersPerDegreeLatitude = 111320;
        const metersPerDegreeLongitude = Math.max(1, Math.abs(metersPerDegreeLatitude * Math.cos(referencePoint[0] * radians)));

        return {
            toXY: point => ({
                x: (point[1] - referencePoint[1]) * metersPerDegreeLongitude,
                y: (point[0] - referencePoint[0]) * metersPerDegreeLatitude
            }),
            toLatLng: point => [
                referencePoint[0] + (point.y / metersPerDegreeLatitude),
                referencePoint[1] + (point.x / metersPerDegreeLongitude)
            ]
        };
    }

    function normalizeVector(vector) {
        const length = Math.hypot(vector.x, vector.y);
        if (!Number.isFinite(length) || length < 0.00001) return { x: 1, y: 0 };
        return { x: vector.x / length, y: vector.y / length };
    }

    function directionBetween(fromPoint, toPoint) {
        const localProjection = projection(fromPoint);
        const fromXY = localProjection.toXY(fromPoint);
        const toXY = localProjection.toXY(toPoint);
        return normalizeVector({ x: toXY.x - fromXY.x, y: toXY.y - fromXY.y });
    }

    function buildBalloonPath(fromPoint, toPoint, currentDirection, nextDirection, loopWidth, loopLength) {
        const width = loopWidth > 0 ? loopWidth : 400;
        const length = loopLength > 0 ? loopLength : 600;
        const radius = width / 2;
        const approachLength = 10;
        const localProjection = projection(fromPoint);
        const outgoing = normalizeVector(currentDirection);
        const incoming = normalizeVector(nextDirection);
        const normal = { x: -outgoing.y, y: outgoing.x };
        const destination = localProjection.toXY(toPoint);
        const endForward = destination.x * outgoing.x + destination.y * outgoing.y;
        const signedLateral = destination.x * normal.x + destination.y * normal.y;
        const side = signedLateral >= 0 ? 1 : -1;
        const lateral = Math.abs(signedLateral);
        const entryHeading = Math.atan2((incoming.x * normal.x + incoming.y * normal.y) * side,
            incoming.x * outgoing.x + incoming.y * outgoing.y);
        const arrival = {
            x: endForward - Math.cos(entryHeading) * approachLength,
            y: lateral - Math.sin(entryHeading) * approachLength
        };
        const twoPi = Math.PI * 2;
        const positiveAngle = angle => {
            const value = ((angle % twoPi) + twoPi) % twoPi;
            return twoPi - value < 1e-7 ? 0 : value;
        };
        // Endpoint staggering changes only the straight departure/approach lengths.
        const baseLead = Math.max(0, endForward) + Math.max(approachLength, length - radius);
        let lead = Math.max(baseLead, arrival.x);
        let lastTurn = 1;
        let endCenter = {
            x: arrival.x - radius * Math.sin(entryHeading),
            y: arrival.y + radius * Math.cos(entryHeading)
        };
        let tangentHeading = Math.atan2(endCenter.y - radius, endCenter.x - lead);
        let firstSweep = positiveAngle(tangentHeading);
        let lastSweep = positiveAngle(entryHeading - tangentHeading);
        // A wide jump needs one turn towards the next strip, with no reverse loop.
        const wideTurn = lateral >= width - 0.01 && endCenter.y >= radius - 0.01 &&
            firstSweep <= Math.PI + 1e-4 && lastSweep <= Math.PI + 1e-4;
        if (!wideTurn) {
            // Start the departure balloon towards the next strip, then counter-turn to align.
            // The internal tangent needs center spacing of at least one balloon width.
            lastTurn = -1;
            endCenter = {
                x: arrival.x + radius * Math.sin(entryHeading),
                y: arrival.y - radius * Math.cos(entryHeading)
            };
            const centerLateral = endCenter.y - radius;
            const minimumLead = endCenter.x + Math.sqrt(Math.max(0, width * width - centerLateral * centerLateral));
            lead = Math.max(lead, minimumLead);
            const centerDistance = Math.hypot(endCenter.x - lead, centerLateral);
            tangentHeading = Math.atan2(centerLateral, endCenter.x - lead) +
                Math.asin(Math.min(1, width / centerDistance));
            firstSweep = positiveAngle(tangentHeading);
            lastSweep = -positiveAngle(tangentHeading - entryHeading);
        }
        const startCenter = { x: lead, y: radius };
        const curve = [{ x: 0, y: 0 }];
        if (lead > 0) curve.push({ x: lead, y: 0 });
        const addArc = (center, startAngle, sweep) => {
            if (Math.abs(sweep) < 1e-10) return;
            const steps = Math.max(1, Math.ceil(Math.abs(sweep) * radius / 8));
            const fractions = [];
            for (let index = 1; index <= steps; index++) {
                fractions.push(index / steps);
            }
            // Include circle extrema so the rendered diameter reaches the exact configured width.
            for (let quadrant = 0; quadrant < 4; quadrant++) {
                const distance = positiveAngle(Math.sign(sweep) * (quadrant * Math.PI / 2 - startAngle));
                if (distance > 1e-10 && distance < Math.abs(sweep)) fractions.push(distance / Math.abs(sweep));
            }
            fractions.sort((a, b) => a - b).forEach(fraction => {
                const angle = startAngle + sweep * fraction;
                curve.push({ x: center.x + radius * Math.cos(angle), y: center.y + radius * Math.sin(angle) });
            });
        };
        addArc(startCenter, -Math.PI / 2, firstSweep);
        const finalStartAngle = tangentHeading - lastTurn * Math.PI / 2;
        curve.push({
            x: endCenter.x + radius * Math.cos(finalStartAngle),
            y: endCenter.y + radius * Math.sin(finalStartAngle)
        });
        addArc(endCenter, finalStartAngle, lastSweep);
        curve.push(arrival, { x: endForward, y: lateral });
        return { path: curve.map(point => localProjection.toLatLng({
            x: outgoing.x * point.x + normal.x * point.y * side,
            y: outgoing.y * point.x + normal.y * point.y * side
        })) };
    }

    function samePoint(pointA, pointB) {
        return pointA && pointB &&
            Math.abs(pointA[0] - pointB[0]) < 1e-12 &&
            Math.abs(pointA[1] - pointB[1]) < 1e-12;
    }

    function cloneLine(line, index, coordinateOrder) {
        let rawPoints = Array.isArray(line)
            ? line
            : (line && (line.coordinates || line.points || line.line || line.geometry));
        if (rawPoints && !Array.isArray(rawPoints)) {
            if (rawPoints.type === 'Feature' && rawPoints.geometry) rawPoints = rawPoints.geometry;
            rawPoints = rawPoints && rawPoints.coordinates;
        }
        if (!Array.isArray(rawPoints)) return null;

        const points = [];
        for (let i = 0; i < rawPoints.length; i++) {
            const point = normalizePoint(rawPoints[i], coordinateOrder);
            if (point) points.push(point);
        }
        if (points.length < 2) return null;

        const rawKey = line && !Array.isArray(line) ? line.key : null;
        const keyValue = rawKey === null || rawKey === undefined || rawKey === '' ? null : finite(rawKey);
        return {
            key: keyValue === null ? index : keyValue,
            keyIsExplicit: keyValue !== null,
            points: points
        };
    }

    function joinStripSegments(lines) {
        const groups = [];
        const groupsByKey = new Map();
        lines.forEach(line => {
            let group = line.keyIsExplicit ? groupsByKey.get(line.key) : null;
            if (!group) {
                group = { key: line.key, segments: [] };
                groups.push(group);
                if (line.keyIsExplicit) groupsByKey.set(line.key, group);
            }
            group.segments.push(line.points);
        });

        return groups.map(group => {
            if (group.segments.length === 1) return { key: group.key, points: group.segments[0] };

            const reference = group.segments.find(points => !samePoint(points[0], points[points.length - 1])) || group.segments[0];
            const localProjection = projection(reference[0]);
            const direction = directionBetween(reference[0], reference[reference.length - 1]);
            const position = point => {
                const xy = localProjection.toXY(point);
                return (xy.x * direction.x) + (xy.y * direction.y);
            };
            const segments = group.segments.map(points => {
                const first = position(points[0]);
                const last = position(points[points.length - 1]);
                return {
                    position: Math.min(first, last),
                    points: last < first ? points.slice().reverse() : points
                };
            }).sort((left, right) => left.position - right.position);
            const points = [];
            segments.forEach(segment => {
                segment.points.forEach(point => {
                    if (!samePoint(points[points.length - 1], point)) points.push(point);
                });
            });
            return { key: group.key, points: points };
        });
    }

    function orderLines(lines, strategy, lineIndex, loopWidth, stripWidth) {
        const mode = String(strategy || 'BACKTOBACK').toUpperCase();

        if (mode === 'BACKTOBACK') return lines.slice();

        if (mode === 'SKIPBACKTOBACK') {
            const odd = lines.filter(item => item.key % 2 === 1 || item.key % 2 === -1);
            const even = lines.filter(item => item.key % 2 === 0);
            return lineIndex % 2 === 0 ? odd.concat(even) : even.concat(odd);
        }

        if (mode === 'QUICKRACETRACK') {
            const ordered = [];
            const half = Math.ceil(lines.length / 2);
            for (let i = 0; i < half; i++) {
                ordered.push(lines[i]);
                if (lines.length > half + i) ordered.push(lines[half + i]);
            }
            return ordered;
        }

        if (mode === 'REVERSERACETRACK') {
            const ordered = [];
            const half = Math.ceil(lines.length / 2);
            const start = Math.floor(lines.length / 2);
            for (let i = 0; i < half; i++) {
                ordered.push(lines[start + i]);
                if (i < start) ordered.push(lines[i]);
            }
            return ordered;
        }

        if (mode === 'RACETRACK') {
            if (
                loopWidth === null || loopWidth <= 0 ||
                stripWidth === null || stripWidth <= 0 ||
                loopWidth * 2 >= lines.length * stripWidth
            ) {
                const fallback = orderLines(lines, 'QUICKRACETRACK', lineIndex, loopWidth, stripWidth);
                return fallback;
            }

            const linesPerSide = Math.max(1, Math.ceil(loopWidth / stripWidth));
            const groupSize = linesPerSide * 2;
            const partCount = Math.floor(lines.length / groupSize);
            if (partCount <= 0) {
                const fallback = orderLines(lines, 'QUICKRACETRACK', lineIndex, loopWidth, stripWidth);
                return fallback;
            }

            let ordered = [];
            let finalIndex = -1;
            for (let i = 1; i <= partCount; i++) {
                const end = (i * groupSize) - 1;
                const start = end + 1 - groupSize;
                finalIndex = end;
                const group = lines.slice(start, end + 1);
                ordered = ordered.concat(orderLines(
                    group,
                    ordered.length > 0 ? 'REVERSERACETRACK' : 'QUICKRACETRACK',
                    lineIndex,
                    loopWidth,
                    stripWidth
                ));
            }

            const remainder = lines.slice(finalIndex + 1);
            return ordered.concat(orderLines(
                remainder,
                'REVERSERACETRACK',
                lineIndex,
                loopWidth,
                stripWidth
            ));
        }

        if (mode === 'EXPAND') {
            const ordered = [];
            const size = lines.length;
            const half = Math.floor(size / 2);
            for (let i = 0; i < half; i++) {
                if (lineIndex % 2 === 0) ordered.push(lines[(size - half) + i]);
                if (size - i > half) ordered.push(lines[(size - half - 1) - i]);
                if (lineIndex % 2 !== 0) ordered.push(lines[(size - half) + i]);
            }
            return ordered;
        }

        if (mode === 'SQUEEZE') {
            const ordered = [];
            const size = lines.length;
            const half = Math.floor(size / 2);
            for (let i = 0; i < half; i++) {
                ordered.push(lines[i]);
                if (size - i > half) ordered.push(lines[size - 1 - i]);
            }
            return ordered;
        }

        return lines.slice();
    }

    const coordinateOrder = input.coordinateOrder === 'leaflet' ? 'leaflet' : 'geojson';
    const rawLines = Array.isArray(input.passes)
        ? input.passes
        : (Array.isArray(input.faixas) ? input.faixas : []);
    const lines = [];
    for (let i = 0; i < rawLines.length; i++) {
        const line = cloneLine(rawLines[i], i, coordinateOrder);
        if (line) lines.push(line);
    }

    const lineIndexValue = finite(input.lineIndex);
    const lineIndex = lineIndexValue === null ? 0 : Math.trunc(lineIndexValue);
    const strategyMode = String(input.strategy || 'BACKTOBACK').toUpperCase();
    const loopWidth = finite(input.loopWidthM !== undefined ? input.loopWidthM : input.size_loop);
    const loopLength = finite(input.loopLengthM !== undefined ? input.loopLengthM : input.size_h_loop);
    const stripWidth = finite(input.stripWidthM !== undefined ? input.stripWidthM : input.faixa);
    const groupedLines = joinStripSegments(lines);
    const orderedLines = input.preservePassOrder === true
        ? groupedLines
        : orderLines(groupedLines, strategyMode, lineIndex, loopWidth, stripWidth);
    const startPoint = normalizePoint(input.startPoint || input.start, coordinateOrder);
    const endPoint = normalizePoint(input.endPoint || input.end, coordinateOrder);
    const path = [];

    function append(point) {
        if (point && !samePoint(path[path.length - 1], point)) path.push([point[0], point[1]]);
    }

    let currentPoint = null;
    let currentDirection = null;
    let balloonCount = 0;
    for (let i = 0; i < orderedLines.length; i++) {
        const points = orderedLines[i].points;
        const first = points[0];
        const last = points[points.length - 1];
        let reverse = false;

        if (i === 0 && startPoint) {
            reverse = distanceSquared(startPoint, last) < distanceSquared(startPoint, first);
        } else if (currentPoint) {
            reverse = distanceSquared(currentPoint, last) < distanceSquared(currentPoint, first);
            const nextStart = reverse ? last : first;
            const nextDirection = directionBetween(
                nextStart,
                reverse ? points[points.length - 2] : points[1]
            );
            const directionDot = (currentDirection.x * nextDirection.x) +
                (currentDirection.y * nextDirection.y);

            // Keep the nearest endpoint unless it would continue in the same direction.
            if (directionDot > 0) reverse = !reverse;
        } else if (i === orderedLines.length - 1 && endPoint) {
            reverse = distanceSquared(endPoint, first) < distanceSquared(endPoint, last);
        }

        const oriented = reverse ? points.slice().reverse() : points;
        if (i === 0 && startPoint) append(startPoint);

        if (i > 0 && currentPoint && oriented.length >= 2) {
            const nextStart = oriented[0];
            const nextDirection = directionBetween(nextStart, oriented[1]);
            const previousDirection = currentDirection || directionBetween(oriented[0], oriented[1]);
            const balloonPath = buildBalloonPath(
                currentPoint,
                nextStart,
                previousDirection,
                nextDirection,
                loopWidth,
                loopLength
            );

            for (let j = 0; j < balloonPath.path.length; j++) append(balloonPath.path[j]);

            balloonCount++;
        }

        for (let j = 0; j < oriented.length; j++) append(oriented[j]);
        currentPoint = oriented[oriented.length - 1];
        currentDirection = directionBetween(oriented[Math.max(0, oriented.length - 2)], currentPoint);
    }

    if (endPoint) append(endPoint);

    return {
        path: path,
        passCount: orderedLines.length,
        balloonCount: balloonCount,
        strategy: strategyMode
    };
}

/**
 * Generate a Leaflet-ready path off the UI thread where Web Workers are allowed.
 * The input contains already-generated strip lines, not just a polygon outline.
 * Balloon turns connect strips using loopWidthM/size_loop and loopLengthM/size_h_loop.
 * Set preservePassOrder to true for preordered strips; startPoint orients the first strip.
 *
 * @example
 * generateFlightPath2D({
 *   passes: [{ key: 0, coordinates: [[-46.5, -23.5], [-46.49, -23.5]] }],
 *   strategy: 'BACKTOBACK'
 * }).then(({ path }) => L.polyline(path));
 */
export function generateFlightPath2D(input) {
    return new Promise((resolve, reject) => {
        if (
            typeof Worker === 'undefined' ||
            typeof Blob === 'undefined' ||
            typeof URL === 'undefined' ||
            typeof URL.createObjectURL !== 'function'
        ) {
            try {
                resolve(buildFlightPath2D(input));
            } catch (error) {
                reject(error);
            }
            return;
        }

        let worker = null;
        let workerUrl = null;
        let completed = false;

        const cleanup = () => {
            if (worker) worker.terminate();
            if (workerUrl) URL.revokeObjectURL(workerUrl);
            worker = null;
            workerUrl = null;
        };

        const fallback = () => {
            if (completed) return;
            completed = true;
            cleanup();
            try {
                resolve(buildFlightPath2D(input));
            } catch (error) {
                reject(error);
            }
        };

        try {
            const workerSource = [
                'var buildFlightPath2D = ' + buildFlightPath2D.toString() + ';',
                'self.onmessage = function(event) {',
                '  try { self.postMessage({ result: buildFlightPath2D(event.data) }); }',
                '  catch (error) { self.postMessage({ error: error && error.message ? error.message : String(error) }); }',
                '};'
            ].join('\n');
            workerUrl = URL.createObjectURL(new Blob([workerSource], { type: 'application/javascript' }));
            worker = new Worker(workerUrl);
            worker.onmessage = (event) => {
                if (completed) return;
                completed = true;
                cleanup();
                if (event.data && event.data.error) {
                    reject(new Error(event.data.error));
                    return;
                }
                resolve(event.data && event.data.result);
            };
            worker.onerror = fallback;
            worker.onmessageerror = fallback;
            worker.postMessage(input || {});
        } catch (error) {
            fallback();
        }
    });
}

export default generateFlightPath2D;
